import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, TRAMITES, TRAMITE_LIST, MXN } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Save } from "lucide-react";

export default function ClientDetailModal({ clientId, onClose, onChanged }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tramiteTab, setTramiteTab] = useState(null);

  const fetchClient = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}`);
      setClient(r.data);
      setTramiteTab(r.data.tramite || TRAMITE_LIST[0].key);
    } catch {
      toast.error("No se pudo cargar el cliente");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
    // eslint-disable-next-line
  }, [clientId]);

  const patch = (partial) => setClient((c) => ({ ...c, ...partial }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        comentarios: client.comentarios,
        tramite: tramiteTab,
        cobro: Number(client.cobro || 0),
      };
      await api.put(`/clients/${clientId}`, body);
      toast.success("Cambios guardados");
      onChanged?.();
      onClose();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Cliente eliminado");
      onChanged?.();
      onClose();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="client-detail-modal" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {loading ? "Cargando…" : client?.nombre_completo}
          </DialogTitle>
        </DialogHeader>

        {!loading && client && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <Info label="NSS" value={client.nss} mono />
              <Info label="CURP" value={client.curp} mono />
              <Info label="Zona" value={client.zona} />
              <Info label="Teléfono" value={client.telefono || "—"} />
              <Info label="Email" value={client.email || "—"} />
              <Info label="Dirección" value={client.direccion || "—"} />
              <Info label="Fecha de registro" value={fmtDate(client.fecha_registro || client.created_at)} />
              <Info label="Fecha de ejecución" value={fmtDate(client.fecha_ejecucion)} />
              <Info label="Fecha de cobranza" value={fmtDate(client.fecha_cobranza)} />
            </div>

            <div>
              <Label className="mb-2 block">Tipo de trámite</Label>
              <Tabs value={tramiteTab} onValueChange={setTramiteTab}>
                <TabsList className="flex-wrap h-auto p-1 bg-neutral-100 gap-1">
                  {TRAMITE_LIST.map((tr) => (
                    <TabsTrigger
                      key={tr.key}
                      value={tr.key}
                      data-testid={`detail-tab-${tr.key}`}
                      className="rounded-lg data-[state=active]:text-white"
                      style={tramiteTab === tr.key ? { backgroundColor: tr.color, color: "#fff" } : {}}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${tr.dot}`} />
                      {tr.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {TRAMITE_LIST.map((tr) => (
                  <TabsContent key={tr.key} value={tr.key} className="mt-4">
                    <div
                      className="p-4 rounded-xl border-l-4"
                      style={{ borderColor: tr.color, backgroundColor: `${tr.color}12` }}
                    >
                      <div className="text-sm font-semibold" style={{ color: tr.color }}>{tr.label}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">Registra el trámite asignado. La cobranza se administra en la sección "Cobranza".</div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </div>

            <div>
              <Label>Cobro estimado (MXN)</Label>
              <Input
                data-testid="detail-cobro"
                type="number"
                step="0.01"
                value={client.cobro || ""}
                onChange={(e) => patch({ cobro: e.target.value })}
                className="font-mono max-w-xs"
                placeholder="0.00"
              />
              <div className="text-xs text-yellow-700 mt-1 font-mono">{MXN(client.cobro)}</div>
            </div>

            <div>
              <Label>Comentarios</Label>
              <Textarea
                data-testid="detail-comentarios"
                rows={5}
                value={client.comentarios || ""}
                onChange={(e) => patch({ comentarios: e.target.value })}
                placeholder="Notas y seguimiento…"
                className="bg-neutral-50"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="detail-delete-open">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar cliente
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar a {client.nombre_completo}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción no se puede deshacer y se perderán todos los datos del cliente.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction data-testid="detail-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">
                      Sí, eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Button
                data-testid="detail-save"
                onClick={save}
                disabled={saving}
                className="bg-black hover:bg-neutral-900 text-yellow-400"
              >
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-neutral-400">{label}</div>
      <div className={`text-sm text-neutral-800 truncate ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
